import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import { CheckCircle, ExclamationOctagon } from 'react-bootstrap-icons';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Close from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import useAuth from '../../authentication/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useEmailService } from '../../services/email/useEmailService';
import {
	LIVE_LIST_NAMES,
	useLiveListApi,
} from '../../apis/hooks/useLiveListsApi';
import EmailRedirectHint from '../EmailRedirectHint';
import ValidationNotification from '../ValidationNotification';
import { FeedbackSubmissionTemplate } from '../../utils/forms';
import AlertModal from '../../components/Alert';
import { isValidEmail } from '../../utils/validation';
import FormSubmitOverlay, {
	FORM_SUBMIT_OVERLAY_MODES,
} from '../FormSubmitOverlay';

const StyledTextField = styled(TextField)(({ isValid, theme }) => ({
	marginTop: '.15rem',
	'& .MuiOutlinedInput-root': {
		'& fieldset': {
			borderColor: isValid ? '' : theme.palette.danger.main,
		},
		'&:hover fieldset': {
			borderColor: isValid ? '' : theme.palette.danger.main,
		},
		'&.Mui-focused fieldset': {
			borderColor: isValid ? '' : theme.palette.danger.main,
		},
	},
}));

function FeedbackModal({ open, onClose, locales }) {
	const theme = useTheme();
	const { t } = useTranslation('contact_us');
	const isMountedRef = useIsMountedRef();
	const { getCurrentUser } = useAuth();
	const currentUser = getCurrentUser();
	const { get: getLiveList } = useLiveListApi();
	const [email, setEmail] = useState(currentUser?.email || '');
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [submitHasError, setSubmitHasError] = useState(false);
	const [feedbackEmail, setFeedbackEmail] = useState('');
	const [allowContact, setAllowContact] = useState(true);
	const [message, setMessage] = useState('');
	const [isShown, setIsShown] = useState(false);
	const [isSubmitClicked, setIsSubmitClicked] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const feedbackSubmissionTemplateName = FeedbackSubmissionTemplate();
	const { sendEmail, actualUserEmail, isEmailRedirectEnabled } =
		useEmailService();
	const showRecipientRedirectHint =
		isEmailRedirectEnabled && Boolean(actualUserEmail);

	const groupEmail = (
		<Link href={`mailto:${feedbackEmail}?subject=Feedback from CDP`}>
			{feedbackEmail}
		</Link>
	);

	useEffect(() => {
		if (currentUser?.email) {
			setEmail((previousEmail) => previousEmail || currentUser.email);
		}
	}, [currentUser?.email]);

	// --------------------------------------------------------------------------------
	// Fetch the feedback recipient email from the live list when the modal is opened
	// --------------------------------------------------------------------------------
	useEffect(() => {
		let isActive = true;

		const fetchFeedbackRecipientEmail = async () => {
			if (!open) {
				return;
			}

			const response = await getLiveList({
				listName: LIVE_LIST_NAMES.EMAIL_TEMPLATES,
			});

			if (!isActive || response?.isAborted || response?.isError) {
				return;
			}

			const items = Array.isArray(response?.data?.items)
				? response.data.items
				: [];
			const matchingTemplate = items.find(
				(item) => item?.value === feedbackSubmissionTemplateName
			);
			const recipientEmailAddress =
				matchingTemplate?.metadata?.recipientEmailAddress;

			if (isActive && recipientEmailAddress) {
				setFeedbackEmail(recipientEmailAddress);
			}
		};

		fetchFeedbackRecipientEmail();

		return () => {
			isActive = false;
		};
	}, [feedbackSubmissionTemplateName, getLiveList, open]);

	const onFocus = () => {
		setIsSubmitClicked(false);
	};

	const handleClose = useCallback(() => {
		setIsShown(false);
		setHasSubmitted(false);
		setSubmitHasError(false);
		onClose();
	}, [onClose]);

	useEffect(() => {
		if (!open) {
			setIsShown(false);
		}
	}, [open]);

	const handleClick = () => {
		setIsShown((current) => !current);
	};

	const fetchFeedback = useCallback(async () => {
		if (isSubmitting || !isMountedRef.current) {
			return;
		}

		setIsSubmitting(true);

		const trimmedMessage = message ? message.trim() : '';

		const response = await sendEmail({
			to: feedbackEmail,
			locale: locales,
			templateName: feedbackSubmissionTemplateName,
			customized: {
				submittedByFirstName: currentUser?.firstName,
				submittedByLastName: currentUser?.lastName,
				companyName: currentUser?.company,
				contactAllowed: String(allowContact),
				contactEmail: email,
				message: trimmedMessage,
			},
		});

		setIsSubmitting(false);

		if (!isMountedRef.current) {
			return;
		}

		const isSuccessfulResponse =
			Boolean(response) && !response?.isAborted && !response?.isError;
		setSubmitHasError(!isSuccessfulResponse);
		setHasSubmitted(true);
		onClose();
		setMessage('');
		setEmail(currentUser?.email || '');
	}, [
		isMountedRef,
		isSubmitting,
		sendEmail,
		currentUser,
		email,
		locales,
		allowContact,
		message,
		feedbackEmail,
		feedbackSubmissionTemplateName,
		onClose,
	]);

	const handleSubmit = (event) => {
		event.preventDefault();
		if (isSubmitting) {
			return null;
		}
		setIsSubmitClicked(true);
		if (!message || (email && !isValidEmail(email))) {
			return null;
		}
		return fetchFeedback();
	};

	return (
		<div>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 600,
						height: { xs: '100%', sm: 'auto' },
						maxWidth: '100%',
						bgcolor: 'background.paper',
						boxShadow: 24,
						border: `1px solid ${theme.palette.base.border}`,
						borderRadius: theme.palette.borderRadius.Md,
						p: theme.palette.spacing.Md,
					}}
				>
					<Grid container spacing={2} p={3}>
						<Grid
							item
							xs={12}
							align="right"
							sx={{ margin: '-2.1rem -2rem auto auto' }}
						>
							<IconButton
								aria-label="clear icon button"
								aria-hidden="false"
								role="button"
								disableRipple
								onClick={handleClose}
							>
								<Close
									fontSize="medium"
									role="graphics-symbol"
									aria-hidden="true"
									aria-label="clear icon"
								/>
							</IconButton>
						</Grid>
						<Grid
							item
							xs={12}
							sx={{
								marginTop: '-1.75rem',
								textAlign: { xs: 'left', sm: 'center' },
							}}
						>
							<Typography id="modal-title" variant="h2">
								{t('feedback_modal.title')}
							</Typography>
							<Typography
								id="modal-subtitle"
								sx={{
									fontSize: theme.palette.textSize.sizeMd,
									marginTop: '-.5rem',
								}}
							>
								{t('feedback_modal.subtitle')}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<form onSubmit={handleSubmit}>
								<FormControl variant="outlined" fullWidth>
									<Grid item xs={12}>
										<Typography
											sx={{
												color: theme.palette.base.contrastText,
												fontWeight: theme.palette.fontWeight.bold,
												fontSize: theme.palette.textSize.sizeMd,
											}}
											aria-label={t('email_fields.to')}
										>
											{t('feedback_modal.experience_prompt')}
										</Typography>
										<StyledTextField
											fullWidth
											id="message"
											role="textbox"
											inputProps={{
												'aria-label': 'contact-us-drawer-text',
											}}
											onChange={(event) => setMessage(event.target.value)}
											value={message}
											variant="outlined"
											size="small"
											margin="normal"
											multiline
											rows={3.3}
											isValid={!isSubmitClicked || (isSubmitClicked && message)}
											onFocus={onFocus}
										/>

										{isSubmitClicked && !message && !hasSubmitted && (
											<ValidationNotification
												ValidMsg={t('feedback_modal.required_feedback')}
											/>
										)}
									</Grid>
									<Grid
										item
										xs={12}
										sx={{ marginTop: theme.palette.spacing.Md }}
									>
										<FormLabel
											sx={{
												color: theme.palette.base.contrastText,
												fontWeight: theme.palette.fontWeight.bold,
												fontSize: theme.palette.textSize.sizeMd,
											}}
										>
											{t('feedback_modal.contact_prompt')}
										</FormLabel>
										<RadioGroup
											aria-labelledby="email_consent_radio_buttons"
											value={allowContact ? 'yes' : 'no'}
											onChange={(event) =>
												setAllowContact(event.target.value === 'yes')
											}
											name="radio-buttons-group"
											id="consent"
										>
											<FormControlLabel
												value="yes"
												control={<Radio />}
												label="Yes, email me"
											/>
											<FormControlLabel
												value="no"
												control={<Radio />}
												label="No"
												sx={{ marginTop: `-${theme.palette.spacing.Sm}` }}
											/>
										</RadioGroup>
									</Grid>
									<Grid item xs={12} sx={{ marginTop: '.5rem' }}>
										<Typography id="user_email">
											{currentUser?.email}{' '}
											<span
												onClick={handleClick}
												role="button"
												tabIndex={0}
												style={{
													color: theme.palette.link.main,
													cursor: 'pointer',
												}}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														handleClick();
													}
												}}
											>
												{t('feedback_modal.use_different_email')}
											</span>
										</Typography>
										{isShown && (
											<>
												<Typography
													id="use_different_email"
													sx={{
														color: theme.palette.base.contrastText,
														fontWeight: theme.palette.fontWeight.bold,
														fontSize: theme.palette.textSize.sizeMd,
														marginTop: theme.palette.spacing.Sm,
													}}
												>
													Email
												</Typography>
												<StyledTextField
													fullWidth
													id="Email"
													onChange={(event) => setEmail(event.target.value)}
													onKeyDown={(event) => event.stopPropagation()}
													value={email}
													variant="outlined"
													size="small"
													margin="normal"
													isValid={
														!isSubmitClicked ||
														!email ||
														(isSubmitClicked && isValidEmail(email))
													}
													inputProps={{
														'aria-label': 'feedback_email',
													}}
												/>
											</>
										)}

										{isSubmitClicked && email && !isValidEmail(email) && (
											<ValidationNotification ValidMsg={t('validation')} />
										)}
									</Grid>
									<Grid
										item
										xs={12}
										sx={{ marginTop: theme.palette.spacing.Xl }}
									>
										<Typography
											id="modal-subtitle"
											sx={{
												fontSize: theme.palette.textSize.sizeMd,
												marginTop: isShown ? '-1rem' : '-.5rem',
											}}
										>
											{t('feedback_modal.order_shipment_questions')}
										</Typography>
									</Grid>
									<Grid
										item
										xs={12}
										align="center"
										sx={{ marginTop: theme.palette.spacing.Xs }}
									>
										<Grid
											item
											container
											spacing={2}
											direction="row"
											justify="flex-start"
										>
											<Grid item xs={12} sm={6} order={{ xs: 2, sm: 1 }}>
												<Button
													fullWidth
													variant="outlined"
													color="primary"
													size="large"
													role="button"
													data-testid="feedback_btn_cancel"
													aria-label="feedback_btn_cancel"
													onClick={handleClose}
												>
													Cancel
												</Button>
											</Grid>
											<Grid item xs={12} sm={6} order={{ xs: 1, sm: 2 }}>
												<Button
													fullWidth
													variant="contained"
													color="primary"
													size="large"
													role="button"
													type="submit"
													data-testid="feedback_btn_submit"
													aria-label="feedback_btn_submit"
													disableElevation
													disabled={isSubmitting}
												>
													<EmailRedirectHint
														show={showRecipientRedirectHint}
														actualUserEmail={actualUserEmail}
														inline
													/>
													Submit
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</FormControl>
							</form>
						</Grid>
						<Grid item xs={12} align="center">
							<Typography
								id="modal-disclaimer"
								sx={{ fontSize: theme.palette.textSize.sizeXs }}
							>
								{t('feedback_modal.disclaimer_prefix')}{' '}
								<a
									href="https://www.smurfitwestrock.com/privacy-notice"
									target="_blank"
								>
									{t('feedback_modal.privacy_policy')}
								</a>
							</Typography>
						</Grid>
					</Grid>
					<FormSubmitOverlay
						open={isSubmitting}
						mode={FORM_SUBMIT_OVERLAY_MODES.MODAL_FORM}
					/>
				</Box>
			</Modal>

			<AlertModal
				icon={
					submitHasError ? (
						<ExclamationOctagon
							style={{ color: theme.palette.danger.main, fontSize: '2rem' }}
						/>
					) : (
						<CheckCircle
							style={{ color: theme.palette.success.main, fontSize: '2rem' }}
						/>
					)
				}
				border={
					submitHasError
						? `1px solid ${theme.palette.danger.border}`
						: `1px solid ${theme.palette.success.border}`
				}
				locales={locales}
				title={
					submitHasError
						? t('feedback_modal.submit_error_title')
						: t('feedback_modal.submit_success_title')
				}
				subtitleOne={
					submitHasError
						? t('feedback_modal.submit_error_message')
						: t('feedback_modal.submit_success_message')
				}
				subtitleTwo={
					submitHasError ? (
						<div>
							{t('feedback_modal.submit_error_contact_prefix')} {groupEmail}
						</div>
					) : null
				}
				open={hasSubmitted}
				onClose={handleClose}
			/>
		</div>
	);
}
FeedbackModal.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	locales: PropTypes.string.isRequired,
};

export default FeedbackModal;
